/**
 * Browser-local state.
 *
 * Everything here is per-browser and non-identifying. There is no account, no cookie
 * used for identity, and nothing here is ever sent to the server except the per-room
 * participant token — and the server only stores that token's keyed hash.
 *
 * Storage can be unavailable (Safari private mode, storage disabled, an embedded
 * webview). Every accessor degrades to "no stored value" rather than throwing, and the
 * room page stays fully usable without it: you simply cannot edit an answer you can no
 * longer prove is yours.
 */

const PREFIX = 'pgb.';
const TOKEN_KEY = (roomId: string) => `${PREFIX}token.${roomId}`;
const CREATOR_KEY = (roomId: string) => `${PREFIX}creator.${roomId}`;
const SEEN_ROOM_KEY = `${PREFIX}seenRoom`;
const ROOMS_CREATED_KEY = `${PREFIX}roomsCreated`;

function read(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* Storage unavailable: the flow still works, editing simply won't persist. */
  }
}

function remove(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

/** 128 bits, base64url, matching the format the API validates. */
function generateToken(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * The token for this browser in this room, creating one if needed.
 *
 * Per room, not per browser: the same browser gets unrelated tokens in two rooms, so
 * the stored verifiers cannot be used to link one person's answers across rooms.
 */
export function getOrCreateParticipantToken(roomId: string): string {
  const existing = read(TOKEN_KEY(roomId));
  if (existing && existing.length >= 20) return existing;

  const token = generateToken();
  write(TOKEN_KEY(roomId), token);
  return token;
}

/** A token generated before the room exists, for the creator's first answer. */
export function generateParticipantToken(): string {
  return generateToken();
}

export function rememberParticipantToken(roomId: string, token: string): void {
  write(TOKEN_KEY(roomId), token);
}

export function getStoredParticipantToken(roomId: string): string | null {
  return read(TOKEN_KEY(roomId));
}

export function forgetRoom(roomId: string): void {
  remove(TOKEN_KEY(roomId));
  remove(CREATOR_KEY(roomId));
}

/* --- experiment markers -------------------------------------------------------- */

/**
 * Booleans and a small counter, nothing else.
 *
 * These answer two questions the experiment lives or dies by — does a recipient later
 * become a creator, and does a creator come back for an unrelated second room — without
 * storing a room id, a profile or anything that could identify a person.
 */

export function markRoomVisitedAsRecipient(): void {
  write(SEEN_ROOM_KEY, '1');
}

export function hasVisitedRoomAsRecipient(): boolean {
  return read(SEEN_ROOM_KEY) === '1';
}

export function markRoomCreated(roomId: string): void {
  write(CREATOR_KEY(roomId), '1');
  const current = Number(read(ROOMS_CREATED_KEY) ?? '0');
  write(ROOMS_CREATED_KEY, String(Number.isFinite(current) ? current + 1 : 1));
}

export function roomsCreatedCount(): number {
  const value = Number(read(ROOMS_CREATED_KEY) ?? '0');
  return Number.isFinite(value) ? value : 0;
}

export function isRoomCreator(roomId: string): boolean {
  return read(CREATOR_KEY(roomId)) === '1';
}
